import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Users, BookOpen, Target } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const EkologosPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/kp2xwc0c_SAS%20Environmental%20Studies.jpeg)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Ekologos
            </h1>
            <p className="text-2xl md:text-3xl mb-4 text-green-100">
              Global Environmental Humanities Network
            </p>
            <p className="text-xl text-green-50 leading-relaxed">
              Bridging indigenous knowledge systems with contemporary climate science across three continents
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">About the Project</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                <strong>Ekologos</strong> is a collaborative project funded by the Norwegian Agency for International 
                Cooperation and Quality Enhancement in Higher Education, coordinated by UiT The Arctic University of Norway. 
                The project brings together institutions from four continents to address critical climate challenges 
                affecting polar, tropical, and highland regions.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                The project engages pedagogically with the critical climate changes affecting the Arctic, South America, 
                South Asia, and Himalaya regions. Through student and staff exchanges, and collaborative research, 
                the objective is to critically engage and amplify the voices of local and indigenous communities and 
                those disproportionately impacted by climate change.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The initiative aims to overcome the limitations of conventional approaches to environmental science 
                and knowledge dissemination by combining Western scientific approaches with the perspectives and 
                knowledge systems of traditional and indigenous communities. This enables the development of new, 
                collaborative methodologies and creates multidisciplinary approaches to foster a new generation of 
                resilient, skilled scholars.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Project Pillars</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>International Collaboration</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Partnerships across Norway, Brazil, India, connecting Arctic, Amazonian, and Himalayan regions
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Indigenous Knowledge</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Amplifying voices of local communities disproportionately affected by climate change
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <BookOpen className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Student Exchanges</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Fellowship programmes enabling scholars to study in Norway, Brazil, and India
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Climate Action</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Collaborative methodologies addressing urgent climate challenges across vulnerable regions
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partner Institutions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Partner Institutions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 border border-gray-200 rounded-lg hover:border-green-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-2">UiT The Arctic University of Norway</h3>
                <p className="text-gray-600 text-sm">Project Coordinator</p>
              </div>
              <div className="p-6 border border-gray-200 rounded-lg hover:border-green-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Institute of Marine Research, Norway</h3>
                <p className="text-gray-600 text-sm">Research Partner</p>
              </div>
              <div className="p-6 border border-gray-200 rounded-lg hover:border-green-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-2">State University of Campinas (UNICAMP), Brazil</h3>
                <p className="text-gray-600 text-sm">South American Partner</p>
              </div>
              <div className="p-6 border border-gray-200 rounded-lg hover:border-green-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-2">RV University, Bangalore</h3>
                <p className="text-gray-600 text-sm">Indian Academic Partner</p>
              </div>
              <div className="p-6 border border-gray-200 rounded-lg hover:border-green-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Doctors For You, Mumbai</h3>
                <p className="text-gray-600 text-sm">Health & Community Partner</p>
              </div>
              <div className="p-6 border border-gray-200 rounded-lg hover:border-green-500 transition-colors">
                <h3 className="text-xl font-bold text-gray-900 mb-2">The Highland Institute, Nagaland</h3>
                <p className="text-gray-600 text-sm">Regional Coordination & Winter School Host</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highland Institute's Role */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Highland Institute's Role</h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Full partner in project coordination and implementation</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Managing the Pluriversity Wiki for knowledge sharing</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Providing fellowships for study in Norway and Brazil</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Hosting Winter School programmes in Nagaland</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Facilitating research in Highland Asia's vulnerable ecosystems</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Research */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Featured Research</h2>
            <Card className="hover:shadow-xl transition-shadow">
              <CardHeader>
                <CardTitle className="text-2xl">Amazon Rainforest Tipping Points</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 mb-4">
                  Prof. Luiz Marques, member of the Ekologos project, investigates tipping points that could 
                  trigger cascading climate effects, such as shifting rainfall patterns, intensifying wildfires, 
                  and accelerating continental forest loss in the Amazon rainforest.
                </p>
                <a
                  href="https://journals.highlanderpress.org/index.php/Highlander/article/view/2/2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
                >
                  Read Full Article
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Get Involved</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Interested in participating in Ekologos fellowship programmes or collaborative research?
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/programmes">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Explore Programmes
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EkologosPage;
